import React from 'react';

import {AnalyticsVisualizationAxisScale as AxisScale} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import YAxisRangeConfigEditor from './YAxisRangeConfigEditor';

type ScatterPlotParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationScatterPlotParametersNode'
>;

type LineChartParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationLineChartParametersNode'
>;

export const Y_AXIS_INCLUDES_0_OVERRIDES_RANGE_WARNING =
  'Including 0 on the y-axis might override the set range';

export interface FormatConfigEditorProps {
  onUpdateParameters: (update: Partial<ScatterPlotParameters | LineChartParameters>) => void;
  parameters: ScatterPlotParameters | LineChartParameters;
}

/**
 * Component for format tab of ScatterPlotConfigEditor
 */
export default function FormatConfigEditor({
  onUpdateParameters,
  parameters,
}: FormatConfigEditorProps): JSX.Element {
  let YAxisMinMaxEditor = null;
  if (parameters.__typename === 'AnalyticsVisualizationScatterPlotParametersNode') {
    YAxisMinMaxEditor = (
      <YAxisRangeConfigEditor
        onUpdateParameters={onUpdateParameters}
        parameters={parameters}
        warning={
          parameters.yAxisIncludesZero && (parameters.yAxisMax || parameters.yAxisMin)
            ? Y_AXIS_INCLUDES_0_OVERRIDES_RANGE_WARNING
            : null
        }
      />
    );
  }

  return (
    <>
      <div className="u-input-spacing-top">
        <FieldLabel label="X-Axis" />
        <CheckboxInput
          id="x-axis-log-scale"
          label="Log scale"
          className="u-mini-spacing-top"
          onChange={(e) => {
            onUpdateParameters({xAxisScale: e.target.checked ? AxisScale.LOG : AxisScale.LINEAR});
          }}
          checked={parameters.xAxisScale === AxisScale.LOG}
        />
      </div>
      <div className="u-input-spacing-top">
        <FieldLabel className="u-small-spacing-top" label="Y-Axis" />
        <CheckboxInput
          id="y-axis-log-scale"
          label="Log scale"
          className="u-mini-spacing-top"
          onChange={(e) => {
            onUpdateParameters({yAxisScale: e.target.checked ? AxisScale.LOG : AxisScale.LINEAR});
          }}
          checked={parameters.yAxisScale === AxisScale.LOG}
        />
        <CheckboxInput
          id="y-axis-includes-0"
          label="Include 0"
          className="u-small-spacing-top"
          onChange={(e) => {
            onUpdateParameters({yAxisIncludesZero: e.target.checked});
          }}
          checked={parameters.yAxisIncludesZero}
        />
        {YAxisMinMaxEditor}
      </div>
    </>
  );
}
